/**
 * Segment summary (rule-based, v1 per Codex feedback 5C).
 *
 * Stitches the 8 traces + per-card outcomes into a calm, factual recap.
 * No LLM call — deterministic template. LLM summary deferred to v1.1.
 *
 * Plays the same soft completion cue on mount as the 5-minute round end so both
 * moments feel like the same ritual.
 */
import { useEffect } from 'react';
import type { ActiveSegment } from '../state/segment';
import type { CardStatus } from '../types';
import { cardStatusFromUserStatus } from '../state/segment';
import { playTimerEndCue } from '../timer/audio';
import { apiPost } from '../api/client';

interface SegmentSummaryProps {
  segment: ActiveSegment;
  onGenerateShare: () => void;
  onExit: () => void;
}

function statsFromSegment(seg: ActiveSegment): Record<CardStatus, number> {
  const stats: Record<CardStatus, number> = { pending: 0, in_progress: 0, done: 0, dropped: 0 };
  // For each card, look at the *latest* trace to decide its outcome.
  for (const card of seg.cards) {
    const last = [...seg.traces].reverse().find((t) => t.cardId === card.id);
    const finalStatus = last ? cardStatusFromUserStatus(last.userStatus) : 'pending';
    stats[finalStatus]++;
  }
  return stats;
}

export function SegmentSummary({ segment, onGenerateShare, onExit }: SegmentSummaryProps) {
  const stats = statsFromSegment(segment);
  const totalRounds = segment.traces.length;

  const outcomeLine: string[] = [];
  if (stats.done) outcomeLine.push(`完成 ${stats.done} 张`);
  if (stats.in_progress) outcomeLine.push(`还在继续 ${stats.in_progress} 张`);
  if (stats.pending) outcomeLine.push(`留到下次 ${stats.pending} 张`);
  if (stats.dropped) outcomeLine.push(`已放下 ${stats.dropped} 张`);

  let closing: string;
  if (stats.in_progress + stats.pending > 0) {
    closing = '还没完成的卡会出现在下次的桌面上,它们不会丢,也不需要从头理解。';
  } else if (stats.done > 0) {
    closing = '桌面已经清空,下次可以从一段新的话开始。';
  } else {
    closing = '这一段结束,下次还能继续。';
  }

  // Same completion cue as 5-minute round end — ritual continuity.
  // Also persists segment completion to D1 (best-effort).
  useEffect(() => {
    playTimerEndCue();
    const ruleSummary = [
      `${totalRounds} 轮`,
      outcomeLine.join('、'),
      closing,
    ].filter(Boolean).join(' · ');
    void apiPost('/api/segment/complete', {
      segmentId: segment.id,
      ruleSummary,
    }).catch((err) => console.warn('[segment/complete] failed', err));
  }, [segment.id]);

  return (
    <section className="panel editorial segment-summary">
      <span className="tiny-label">
        <i className="dot amber" /> 这一段完成了
      </span>
      <h1>这一段经过了 {totalRounds} 轮 5 分钟</h1>
      {outcomeLine.length > 0 && (
        <p className="outcome-line">这一段的桌面:{outcomeLine.join('、')}。</p>
      )}

      <div className="trace-strip">
        {segment.traces.map((t) => (
          <article key={`${t.round}-${t.cardId}`} className="trace-row">
            <header>
              <strong>第 {t.round} 轮 · {t.cardTitle}</strong>
              <span className="muted tiny">{statusLabel(t.userStatus)}</span>
            </header>
            <p className="trace-text">{t.experience.trace}</p>
            {t.experience.nextStart && (
              <p className="next-start muted tiny">↪ {t.experience.nextStart}</p>
            )}
          </article>
        ))}
      </div>

      <p className="closing">{closing}</p>

      <div className="actions">
        <button className="btn-lime" type="button" onClick={onGenerateShare}>
          生成分享卡片
        </button>
        <button className="btn-ghost" type="button" onClick={onExit}>
          回桌面
        </button>
      </div>
    </section>
  );
}

function statusLabel(s: string): string {
  switch (s) {
    case 'done': return '完成了';
    case 'in_progress': return '还在继续';
    case 'pending': return '留到下次';
    case 'drop': return '已放下';
    default: return s;
  }
}
